"use client";

import React, { useState, useEffect } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { toast } from "react-toastify";
import Api from "../../api/Api";

//Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";
import CampaignCategoryTableItem from "./CampaignCategoryTableItem";
import CampaignCategoryModal from "./CampaignCategoryModal";

const CampaignCategories = () => {
  const [modal_center, setmodal_center] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationData, setPaginationData] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
  });

  document.title = "Campaign Categories | Tafaseel Admin";

  const statusOptions = [
    { label: "All Statuses", value: "" },
    { label: "Active", value: "1" },
    { label: "Inactive", value: "0" },
  ];

  useEffect(() => {
    fetchCategories();
  }, [currentPage, searchQuery, statusFilter, dateFrom, dateTo]);

  // Fetch categories from API
  const fetchCategories = async () => {
    try {
      setLoading(true);

      const params = {
        page: currentPage,
        per_page: itemsPerPage,
        search: searchQuery,
        is_active: statusFilter,
        date_from: dateFrom,
        date_to: dateTo,
      };

      // Remove empty parameters
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const token = localStorage.getItem("token");
      const response = await Api.get("/admin/campaign-categories", {
        params,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        setCategories(response.data.data || []);
        if (response.data.pagination) {
          setPaginationData(response.data.pagination);
          setCurrentPage(response.data.pagination.current_page);
          setItemsPerPage(response.data.pagination.per_page);
        } else {
          setPaginationData((prev) => ({
            ...prev,
            total: response.data.data ? response.data.data.length : 0,
            per_page: params.per_page || prev.per_page,
            current_page: params.page || 1,
            last_page: 1,
            from: response.data.data && response.data.data.length > 0 ? 1 : 0,
            to: response.data.data ? response.data.data.length : 0,
          }));
        }
      } else {
        toast.error(response.data.message || "Failed to fetch categories", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Network error. Please check your connection.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    } finally {
      setLoading(false);
    }
  };

  function tog_center() {
    setmodal_center(!modal_center);
    if (modal_center) {
      setSelectedCategory(null);
    }
  }

  const handleAdd = () => {
    setSelectedCategory(null);
    setmodal_center(true);
  };

  const handleEdit = async (category) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.get(
        `/admin/campaign-categories/${category.id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSelectedCategory(response.data.data);
        setmodal_center(true);
      } else {
        toast.error(
          response.data.message || "Failed to fetch category details",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error fetching category details:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleDelete = async (categoryId) => {
    if (
      !window.confirm(
        "Are you sure you want to delete this category? This action cannot be undone."
      )
    ) {
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await Api.delete(
        `/admin/campaign-categories/${categoryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Category deleted successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        // Refresh list to ensure pagination stays accurate
        fetchCategories();
      } else {
        toast.error(response.data.message || "Failed to delete category", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error deleting category:", error);
      if (error.response && error.response.status === 400) {
        toast.error(
          error.response.data.message ||
            "Cannot delete category with associated campaigns",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      } else {
        toast.error("Network error. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    }
  };

  const handleUpdateCategoryStatus = async (categoryId, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      const response = await Api.put(
        `/admin/campaign-categories/${categoryId}/status`,
        {
          is_active: newStatus,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success("Category status updated successfully", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });

        fetchCategories();
      } else {
        toast.error(
          response.data.message || "Failed to update category status",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );
      }
    } catch (error) {
      console.error("Error updating category status:", error);
      toast.error("Network error. Please try again.", {
        position: "bottom-center",
        autoClose: 3000,
        theme: "colored",
      });
    }
  };

  const handleSave = async (categoryData) => {
    try {
      const token = localStorage.getItem("token");
      let response;

      if (selectedCategory) {
        // Update existing category
        response = await Api.put(
          `/admin/campaign-categories/${selectedCategory.id}`,
          categoryData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // Create new category
        response = await Api.post("/admin/campaign-categories", categoryData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }

      if (response.data.success) {
        toast.success(
          selectedCategory
            ? "Category updated successfully"
            : "Category created successfully",
          {
            position: "bottom-center",
            autoClose: 3000,
            theme: "colored",
          }
        );

        tog_center();
        fetchCategories();
      } else {
        toast.error(response.data.message || "Failed to save category", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    } catch (error) {
      console.error("Error saving category:", error);
      if (error.response && error.response.data && error.response.data.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach((key) => {
          errors[key].forEach((message) => {
            toast.error(message, {
              position: "bottom-center",
              autoClose: 3000,
              theme: "colored",
            });
          });
        });
      } else {
        toast.error("Network error. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
          theme: "colored",
        });
      }
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setCurrentPage(1);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    applyFilters();
  };

  const applyFilters = () => {
    setCurrentPage(1);
    setShowFiltersModal(false);
    fetchCategories();
  };

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setSearchQuery("");
    setCurrentPage(1);
  };

  // Get category counts by status
  const getStatusCounts = () => {
    const counts = {
      all: paginationData.total,
      active: categories.filter((c) => c.is_active === 1).length,
      inactive: categories.filter((c) => c.is_active === 0).length,
    };
    return counts;
  };

  const statusCounts = getStatusCounts();

  // Pagination logic based on API response
  const totalPages = paginationData.last_page || 1;
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const setPage = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbers = 10;
    const maxVisiblePages = Math.min(totalPages, maxPageNumbers);

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage < maxVisiblePages - 1) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return pageNumbers;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Tafaseel Admin"
            breadcrumbItem="Campaign Categories"
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id="categoryList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div className="d-flex gap-1">
                          <Button
                            color="success"
                            className="add-btn"
                            onClick={handleAdd}
                          >
                            <i className="ri-add-line align-bottom me-1"></i>
                            Add Category
                          </Button>
                          <Button
                            color="primary"
                            className="refresh-btn"
                            onClick={fetchCategories}
                            disabled={loading}
                          >
                            <i className="ri-refresh-line align-bottom me-1"></i>
                            {loading ? "Loading..." : "Refresh"}
                          </Button>
                          <Button
                            color="info"
                            className="filters-btn"
                            onClick={() => setShowFiltersModal(true)}
                          >
                            <i className="ri-filter-line align-bottom me-1"></i>
                            Advanced Filters
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end gap-2">
                          <div className="status-filter">
                            <select
                              className="form-select"
                              value={statusFilter}
                              onChange={(e) =>
                                handleStatusFilter(e.target.value)
                              }
                            >
                              {statusOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="search-box">
                            <input
                              type="text"
                              className="form-control search"
                              placeholder="Search categories..."
                              value={searchQuery}
                              onChange={handleSearch}
                            />
                          </div>
                        </div>
                      </Col>
                    </Row>

                    {/* Status Summary Cards */}
                    <Row className="mb-3">
                      <Col>
                        <div className="d-flex gap-3 flex-wrap">
                          <div className="status-card p-2 border rounded">
                            <small className="text-muted">Total</small>
                            <div className="fw-bold">{statusCounts.all}</div>
                          </div>
                          <div className="status-card p-2 border rounded border-success">
                            <small className="text-success">Active</small>
                            <div className="fw-bold text-success">
                              {statusCounts.active}
                            </div>
                          </div>
                          <div className="status-card p-2 border rounded border-danger">
                            <small className="text-danger">Inactive</small>
                            <div className="fw-bold text-danger">
                              {statusCounts.inactive}
                            </div>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      {loading ? (
                        <div className="text-center p-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="sr-only">Loading...</span>
                          </div>
                          <p className="mt-2">Loading categories...</p>
                        </div>
                      ) : (
                        <table
                          className="table align-middle table-nowrap"
                          id="categoryTable"
                        >
                          <thead className="table-light">
                            <tr>
                              <th className="sort" data-sort="category_id">
                                ID
                              </th>
                              <th className="sort" data-sort="name">
                                Name (English)
                              </th>
                              <th className="sort" data-sort="arabic_name">
                                Name (Arabic)
                              </th>
                              <th className="sort" data-sort="icon">
                                Icon
                              </th>
                              <th className="sort" data-sort="campaigns_count">
                                Campaigns
                              </th>
                              <th className="sort" data-sort="status">
                                Status
                              </th>
                              <th className="sort" data-sort="created_at">
                                Created
                              </th>
                              <th className="sort" data-sort="action">
                                Action
                              </th>
                            </tr>
                          </thead>
                          <tbody className="list form-check-all">
                            {categories.length > 0 ? (
                              categories.map((category) => (
                                <CampaignCategoryTableItem
                                  key={category.id}
                                  category={category}
                                  handleEdit={handleEdit}
                                  handleDelete={handleDelete}
                                  handleUpdateStatus={
                                    handleUpdateCategoryStatus
                                  }
                                />
                              ))
                            ) : (
                              <tr>
                                <td colSpan="8" className="text-center p-4">
                                  <div className="text-center">
                                    <lord-icon
                                      src="https://cdn.lordicon.com/msoeawqm.json"
                                      trigger="loop"
                                      colors="primary:#121331,secondary:#08a88a"
                                      style={{ width: "75px", height: "75px" }}
                                    ></lord-icon>
                                    <h5 className="mt-2">
                                      No Categories Found
                                    </h5>
                                    <p className="text-muted mb-0">
                                      {searchQuery || statusFilter
                                        ? `No categories found matching your criteria`
                                        : "No categories available. Click 'Add Category' to create one."}
                                    </p>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-center">
                        <div className="pagination-wrap hstack gap-2">
                          <div className="d-flex justify-content-end">
                            <div
                              className="btn-group me-2"
                              role="group"
                              aria-label="Pagination"
                            >
                              <button
                                className="page-item pagination-prev btn btn-primary"
                                onClick={prevPage}
                                type="button"
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>

                              {getPageNumbers().map((pageNumber) => (
                                <button
                                  type="button"
                                  className={`btn ${
                                    pageNumber === currentPage
                                      ? "btn-primary"
                                      : "btn-outline-primary"
                                  }`}
                                  key={pageNumber}
                                  onClick={() => setPage(pageNumber)}
                                >
                                  {pageNumber}
                                </button>
                              ))}

                              <button
                                disabled={currentPage === totalPages}
                                className="page-item pagination-next btn btn-primary"
                                type="button"
                                onClick={nextPage}
                              >
                                Next
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show results info */}
                    {paginationData.total > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Showing {paginationData.from || 0} to{" "}
                          {paginationData.to || categories.length} of{" "}
                          {paginationData.total} categories
                        </div>
                        <div className="text-muted">
                          Page {currentPage} of {totalPages}
                        </div>
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Category Modal */}
          {modal_center && (
            <CampaignCategoryModal
              modal_center={modal_center}
              tog_center={tog_center}
              category={selectedCategory}
              onSave={handleSave}
            />
          )}

          {/* Advanced Filters Modal */}
          {showFiltersModal && (
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Advanced Filters</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFiltersModal(false)}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <Row>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Date From</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                          />
                        </div>
                      </Col>
                      <Col md={6}>
                        <div className="mb-3">
                          <label className="form-label">Date To</label>
                          <input
                            type="date"
                            className="form-control"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                          />
                        </div>
                      </Col>
                    </Row>
                  </div>
                  <div className="modal-footer">
                    <Button color="secondary" onClick={clearFilters}>
                      Clear All
                    </Button>
                    <Button color="primary" onClick={applyFilters}>
                      Apply Filters
                    </Button>
                  </div>
                </div>
              </div>
              <div className="modal-backdrop fade show"></div>
            </div>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default CampaignCategories;
